import {
  doc,
  getDocs,
  collection,
  query,
  orderBy,
  limit,
  updateDoc,
  increment,
  onSnapshot,
  getDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

export interface LeaderboardEntry {
  userId: string;
  username: string;
  handsWon: number;
  rank?: number;
}

export type LeaderboardPeriod = 'allTime' | 'weekly' | 'monthly';

const getFieldName = (period: LeaderboardPeriod): string => {
  switch (period) {
    case 'weekly': return 'handsWonWeekly';
    case 'monthly': return 'handsWonMonthly';
    default: return 'handsWon';
  }
};

export const getLeaderboard = async (period: LeaderboardPeriod = 'allTime', maxResults: number = 50): Promise<LeaderboardEntry[]> => {
  try {
    const fieldName = getFieldName(period);
    const q = query(collection(db, 'users'), orderBy(fieldName, 'desc'), limit(maxResults));
    const snapshot = await getDocs(q);
    const leaderboard: LeaderboardEntry[] = [];
    let rank = 1;
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      leaderboard.push({
        userId: docSnap.id,
        username: data.username || 'Unknown',
        handsWon: data[fieldName] || 0,
        rank: rank++,
      });
    });
    return leaderboard;
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    return [];
  }
};

export const subscribeToLeaderboard = (
  callback: (leaderboard: LeaderboardEntry[]) => void,
  period: LeaderboardPeriod = 'allTime',
  maxResults: number = 10
): (() => void) => {
  const fieldName = getFieldName(period);
  const q = query(collection(db, 'users'), orderBy(fieldName, 'desc'), limit(maxResults));
  return onSnapshot(q, (snapshot) => {
    const leaderboard: LeaderboardEntry[] = [];
    let rank = 1;
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      leaderboard.push({
        userId: docSnap.id,
        username: data.username || 'Unknown',
        handsWon: data[fieldName] || 0,
        rank: rank++,
      });
    });
    callback(leaderboard);
  }, () => callback([]));
};

/**
 *  DEPRECATED: Stats updated server-side in showdown handler (lines 16629-16631)

 */
export const incrementHandsWon = async (userId: string): Promise<void> => {
  // No-op: Stats are now updated securely in Cloud Functions
  console.log(`⚠️ [Leaderboard] incrementHandsWon deprecated - stats updated server-side`);
};

export const getUserRank = async (userId: string, period: LeaderboardPeriod = 'allTime'): Promise<number | null> => {
  try {
    const fieldName = getFieldName(period);
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) return null;
    const userHandsWon = userSnap.data()[fieldName] || 0;
    const q = query(collection(db, 'users'), where(fieldName, '>', userHandsWon));
    const snapshot = await getDocs(q);
    return snapshot.size + 1;
  } catch (error) {
    console.error('Error getting user rank:', error);
    return null;
  }
};